
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useClose } from "@/contexts/CloseContext";
import { AlertCircle, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCloseMetrics } from "@/hooks/useCloseMetrics";
import { formatTimeRemaining } from "@/utils/closeApiRateLimiting";
import { Skeleton } from "@/components/ui/skeleton";
import { API_REQUEST_MODE } from "@/services/closeApi/constants";

export const CloseActivities = () => {
  const { isConnected } = useClose();
  const metrics = useCloseMetrics();

  if (!isConnected) {
    return (
      <Card className="bg-gray-800/20 border-gray-700">
        <CardContent className="p-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Nicht verbunden</h3>
          <p className="text-gray-400 mb-4">
            Verbinden Sie Ihr Close CRM-Konto, um Ihre Aktivitäten zu sehen.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (metrics.error) {
    const isNetworkError = metrics.error.includes('Netzwerkfehler') || 
                           metrics.error.includes('Network Error') || 
                           metrics.error.includes('Failed to fetch');
    
    return (
      <Card className="bg-gray-800/20 border-gray-700">
        <CardContent className="p-6 text-center">
          {isNetworkError ? (
            <WifiOff className="w-10 h-10 text-red-500 mx-auto mb-4" />
          ) : (
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
          )}
          <h3 className="text-lg font-medium mb-2">Fehler beim Laden der Metriken</h3>
          <p className="text-red-400 mb-4">
            {metrics.error}
          </p>
          
          {metrics.remainingCooldown > 0 ? (
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2 text-amber-400 mb-4">
                <span>Nächste Aktualisierung in {formatTimeRemaining(metrics.remainingCooldown)}</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={metrics.refreshMetrics}
                disabled={true}
                className="border-gray-700 opacity-50 cursor-not-allowed"
              >
                <RefreshCw size={16} className="mr-2" />
                Warten Sie bitte
              </Button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              size="sm"
              onClick={metrics.refreshMetrics}
              disabled={metrics.isFetching}
              className="border-gray-700"
            >
              {metrics.isFetching ? (
                <RefreshCw size={16} className="animate-spin mr-2" />
              ) : (
                <RefreshCw size={16} className="mr-2" />
              )}
              Erneut versuchen
            </Button>
          )}
          
          {isNetworkError && (
            <div className="mt-4 text-sm text-gray-400 bg-gray-800/30 p-3 rounded">
              <p>Behebung von Netzwerkfehlern:</p>
              <ul className="list-disc list-inside mt-2 text-left">
                <li>Überprüfen Sie Ihre Internetverbindung</li>
                <li>Deaktivieren Sie CORS-Blocker oder Browser-Erweiterungen</li>
                <li>Öffnen Sie die Entwicklerkonsole (F12) für detaillierte Fehlermeldungen</li>
                <li>Versuchen Sie es mit einem anderen Browser</li>
                <li>Prüfen Sie, ob api.close.com erreichbar ist (direkt im Browser)</li>
              </ul>
              <p className="mt-2 text-gray-500">Technische Info: Diese App verwendet {API_REQUEST_MODE} Modus für API-Anfragen.</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Close CRM Metriken</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={metrics.refreshMetrics}
          disabled={metrics.isFetching || metrics.remainingCooldown > 0}
          className="border-gray-700"
        >
          {metrics.isFetching ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
        </Button>
      </div>

      {metrics.isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array(10).fill(0).map((_, i) => (
            <Card key={i} className="bg-gray-800/20 border-gray-700">
              <CardContent className="p-4">
                <Skeleton className="h-8 w-16 bg-gray-700 mb-2" />
                <Skeleton className="h-6 w-32 bg-gray-700" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <MetricTile 
              value={metrics.newEntries.today} 
              label="Erstellte Leads" 
              subtitle="HEUTE"
            />
            
            <MetricTile 
              value={metrics.callAttempts.today} 
              label="Ausgehende Anrufe" 
              subtitle="HEUTE" 
            />
            
            <MetricTile 
              value={metrics.callsReached.today} 
              label="Eingehende Anrufe" 
              subtitle="HEUTE"
            />
            
            <MetricTile 
              value={`${metrics.callDuration.today}min`} 
              label="Alle Anrufe" 
              subtitle="DURCHSCHN. DAUER HEUTE"
            />
            
            <MetricTile 
              value={metrics.callAttempts.thisWeek} 
              label="Ausgehende Anrufe" 
              subtitle="DIESE WOCHE"
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <MetricTile 
              value={metrics.appointments.today} 
              label="Opportunities" 
              subtitle="ERSTELLT HEUTE"
            />
            
            <MetricTile 
              value={metrics.closedDeals.today} 
              label="Opportunities" 
              subtitle="GEWONNEN HEUTE"
            />
            
            <MetricTile 
              value={`${(metrics.revenue.today).toFixed(0)} €`} 
              label="Opportunities" 
              subtitle="WERT GEWONNEN HEUTE"
            />
            
            <MetricTile 
              value={metrics.newEntries.thisWeek}
              label="Erstellte Leads" 
              subtitle="DIESE WOCHE"
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface MetricTileProps {
  value: number | string;
  label: string;
  subtitle?: string;
}

const MetricTile = ({ value, label, subtitle }: MetricTileProps) => {
  return (
    <Card className="bg-white text-gray-900 border-gray-200 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="text-3xl font-bold text-blue-600 mb-1">{value}</div>
        <div className="flex flex-col">
          <span className="text-gray-700 font-medium">{label}</span>
          {subtitle && <span className="text-gray-500 text-xs">{subtitle}</span>}
        </div>
      </CardContent>
    </Card>
  );
};
